import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { SystemStyleObject } from "@chakra-ui/react";
import colors from "app/colors";
import { CreatableSelect, MultiValue } from "chakra-react-select";
import { IIsNew } from "gql/isNew";
import { isEmpty, uniqBy } from "lodash";
import { NEW_TAG_NAME_REGEX } from "modules/utils/Constants";
import { useTags } from "modules/providers/tags-provider";
import { TagFragment } from "gql/graphql";

interface IProps {
  isDisabled?: boolean;
  parentTagId?: string;
  onChange: (value: readonly INewTagOption[]) => void;
}

export interface INewTagOption extends IIsNew {
  readonly label: string;
  readonly value: string;
  readonly duplicateTag?: TagFragment;
}

const AdminCreateTagSelect = ({
  isDisabled = false,
  parentTagId,
  onChange,
}: IProps) => {
  const { tags } = useTags();
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<readonly INewTagOption[]>([]);

  const _tags = useMemo(
    () => tags.filter((tag) => !parentTagId || tag.parentTagId === parentTagId),
    [parentTagId, tags],
  );

  const getDuplicateTag = useCallback(
    (value: string) => {
      const lowerCaseValue = value.toLowerCase();
      if (parentTagId) {
        return _tags.find(
          (tag) =>
            tag.parentTagId === parentTagId &&
            tag.name.toLowerCase() === lowerCaseValue,
        );
      }
      return _tags.find(
        (tag) => !tag.parentTagId && tag.name.toLowerCase() === lowerCaseValue,
      );
    },
    [_tags, parentTagId],
  );

  const _onChange = useCallback(
    (options: MultiValue<INewTagOption>) => {
      const _newOptions = uniqBy(options, "value");
      setValue(_newOptions);
      onChange(_newOptions);
    },
    [onChange],
  );

  useEffect(() => {
    _onChange(
      value.map((v) => ({
        ...v,
        duplicateTag: getDuplicateTag(v.value),
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentTagId]);

  const getSanitizedValue = useCallback(
    (value: string) =>
      value
        .split("")
        .filter((char) => NEW_TAG_NAME_REGEX.test(char))
        .join(""),
    [],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!inputValue || isEmpty(inputValue)) return;
      switch (e.key) {
        case "Spacebar":
        case " ":
        case "Enter":
        case "Tab":
          _onChange([
            ...value,
            {
              label: inputValue,
              value: inputValue,
              duplicateTag: getDuplicateTag(inputValue),
              __isNew__: true,
            },
          ]);
          setInputValue("");
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    [inputValue, _onChange, value, getDuplicateTag],
  );

  const onBlur = useCallback(() => {
    if (!inputValue || isEmpty(inputValue)) return;
    _onChange([
      ...value,
      {
        label: inputValue,
        value: inputValue,
        duplicateTag: getDuplicateTag(inputValue),
        __isNew__: true,
      },
    ]);
  }, [_onChange, getDuplicateTag, inputValue, value]);

  const _onInputChange = useCallback(
    (newValue: string) => {
      if (newValue.includes(",")) {
        _onChange([
          ...value,
          ...newValue
            .split(",")
            .map((v) => getSanitizedValue(v.toLowerCase()).trim())
            .filter((v) => v.length > 0 && !isEmpty(v))
            .map((v) => ({
              label: v,
              value: v,
              duplicateTag: getDuplicateTag(v),
              __isNew__: true,
            })),
        ]);
        setInputValue("");
        return;
      }
      setInputValue(getSanitizedValue(newValue));
    },
    [_onChange, getDuplicateTag, getSanitizedValue, value],
  );

  const formatOptionLabel = useCallback(
    (option: INewTagOption) => option.label,
    [],
  );

  return (
    <CreatableSelect
      isDisabled={isDisabled}
      isMulti
      isClearable
      menuIsOpen={false}
      inputValue={inputValue}
      onInputChange={_onInputChange}
      onKeyDown={onKeyDown}
      onChange={_onChange}
      formatOptionLabel={formatOptionLabel}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      onBlur={onBlur}
      value={value}
      placeholder="Add values for this category"
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          fontSize: 14,
          color: colors.placeholderText,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "100%",
        }),
        multiValue: (
          provided: SystemStyleObject,
          state,
        ): SystemStyleObject => ({
          ...provided,
          borderRadius: "full",
          borderWidth: "1px",
          borderColor: state.data.duplicateTag ? colors.red : colors.blue,
          backgroundColor: state.data.duplicateTag
            ? colors.redLight
            : colors.white,
          color: state.data.duplicateTag ? colors.red : colors.blue,
          fontSize: 14,
        }),
      }}
    />
  );
};

export default memo(AdminCreateTagSelect);
